/**
 * Rolling balance counter (Phase-3 scaffold). GSAP tweens a numeric object with
 * `snap: { value: 1 }` so it stays on integer cents, formatting cents→dollars
 * only at the display edge (balances are integer cents server-side). Re-rolls
 * whenever the displayed balance changes (e.g. after `result` payouts arrive).
 */

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { formatCents } from '../util/format';

export function BalanceCounter({ cents, className }: { readonly cents: number; readonly className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef<number>(cents);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obj = { value: prev.current };
    const tween = gsap.to(obj, {
      value: cents,
      duration: 0.8,
      ease: 'power1.out',
      snap: { value: 1 },
      onUpdate: () => {
        el.textContent = formatCents(obj.value);
      },
    });
    prev.current = cents;
    return () => {
      tween.kill();
    };
  }, [cents]);

  return <span ref={ref} className={`numeric ${className ?? ''}`}>{formatCents(cents)}</span>;
}
